/**
 * One sortable task list: a SectionHead with its own sort control, and the
 * animated row list beneath it.
 *
 * Replaces the near-identical private `Section` components that Today,
 * Upcoming, Waiting and Calendar each had, and the equivalent inline block in
 * All tasks. They differed only in which optional props they passed; once all
 * five needed the same sort wiring the duplication stopped paying for itself.
 *
 * Presentational and CONTROLLED: sort state lives in the screen and arrives as
 * `sort` / `onSortChange`. That follows the convention `_task-row.ts` states —
 * keep the logic in the screens so the primitives stay presentational — and
 * avoids a controlled/uncontrolled duality that would exist only to serve
 * All tasks, which needs to read the sort to pick its server window.
 */

import { useAutoAnimate } from '@formkit/auto-animate/react';
import { SectionHead, TaskRow, type TaskRowModel } from '@k-os/ui';
import type { TaskDto } from '../api/tasks';
import { toRowModel, type ToRowOptions } from '../screens/_task-row';
import type { useTaskQuickActions } from '../screens/_use-task-quick-actions';
import { sortRows, type TaskSort } from '../lib/task-sort';
import { SortMenu } from './SortMenu';
import styles from './TaskSection.module.css';

export interface TaskSectionProps {
  title: string;
  /** Unsorted rows — this component sorts what it is given. */
  tasks: TaskDto[];
  sort: TaskSort;
  onSortChange: (next: TaskSort) => void;
  quick: ReturnType<typeof useTaskQuickActions>;
  onComplete?: (task: TaskRowModel) => void;
  alert?: string | null;
  showStatus?: boolean;
  /** Scroll anchor — Calendar's count tiles target these. */
  id?: string;
  /** Extra `toRowModel` options; All tasks passes `showUpdatedSince`. */
  rowOptions?: ToRowOptions;
}

export function TaskSection({
  title,
  tasks,
  sort,
  onSortChange,
  quick,
  onComplete,
  alert,
  showStatus = false,
  id,
  rowOptions,
}: TaskSectionProps) {
  // useAutoAnimate hooks into React's reconciliation, so rows glide to their
  // new positions when the sort changes instead of snapping.
  const [listRef] = useAutoAnimate<HTMLDivElement>();

  if (tasks.length === 0) return null;

  return (
    <div className={styles.section} {...(id ? { id } : {})}>
      <SectionHead
        title={title}
        count={tasks.length}
        alert={alert ?? null}
        action={<SortMenu value={sort} onChange={onSortChange} label={title} />}
      />
      <div ref={listRef}>
        {/* Sorting happens HERE, not at the call site. The 17 sections across
         * five screens would otherwise each have to remember to wrap their
         * rows, and forgetting one would silently render an unsorted list that
         * still displays a sort in its header. */}
        {sortRows(tasks, sort).map((task) => (
          <TaskRow
            key={task.id}
            task={toRowModel(task, rowOptions ?? {})}
            showStatus={showStatus}
            {...(onComplete ? { onComplete } : {})}
            {...quick(task)}
          />
        ))}
      </div>
    </div>
  );
}
